import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import "./singleClient.css";
import AdminBDRSpinner from "../../adminComponents/AdminBDRSpinner/AdminBDRSpinner";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import ClientNotesDisplay from "../../adminComponents/ClientNotesDisplay/ClientNotesDisplay";
import {
  GET_CLIENT_BY_ID,
  GET_PHONE_TEXTS_SINCE,
  GET_CLIENT_EMAIL_COUNTS,
} from "../../../../utils/GraphQL/queries";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  useSSEListener,
  SSE_RECONNECTED,
} from "../../../../utils/SSE/SSEContext";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import phoneNumberFormat from "../../../../utils/formatters/phoneNumberFormat";
import {
  UPDATE_PHN_MSGS_READ,
  DELETE_CLIENT,
} from "../../../../utils/GraphQL/mutations";
import ManagerLinkContainer from "./ManagerLinkContainer";
import CreateQuoteModal from "../../adminComponents/CreateQuoteModal/CreateQuoteModal";
import ClientQuotesDisplay from "../../adminComponents/ClientQuotesDisplay/ClientQuotesDisplay";
import ViewClientQuoteModal from "../../adminComponents/ViewClientQuoteModal/ViewClientQuoteModal";
import UserTaskCreate from "../../adminComponents/UserTaskModal/UserTaskCreate";
import ClientTasksDisplay from "../../adminComponents/ClientTasksDisplay/ClientTasksDisplay";
import ClientInvoicesDisplay from "../../adminComponents/ClientInvoicesDisplay/ClientInvoicesDisplay";
import ViewClientInvoiceModal from "../../adminComponents/ViewClientInvoiceModal/ViewClientInvoiceModal";
import SendEmailModal from "../../adminComponents/SendEmail/SendEmailModal";
import ClientTextMesDisplay from "../../adminComponents/ClientTextMesDisplay/ClientTextMesDisplay";
import QuoteInvoiceEditModal from "../../adminComponents/QuoteInvoiceEditModal/QuoteInvoiceEditModal";
import ClientPicsDisplay from "../../adminComponents/ClientPicsDisplay/ClientPicsDisplay";
import ClientEmailDisplay from "../../adminComponents/ClientEmailDisplay/ClientEmailDisplay";
import ForumIcon from "@mui/icons-material/Forum";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import PlaceIcon from "@mui/icons-material/Place";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import PeopleIcon from "@mui/icons-material/People";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import CampaignIcon from "@mui/icons-material/Campaign";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import ClientConsoleTiles from "./ClientConsoleTiles";
import ClientConsoleVS from "./ClientConsoleVS";
import ClientConsolePanelShell from "./ClientConsolePanelShell";
import ClientActivityDisplay from "./ClientActivityDisplay";
import ClientConsoleSummary from "./ClientConsoleSummary";
import ClientFormModal from "../../adminComponents/ClientForm/ClientFormModal";
import ClientFactRow, { addressOneLine } from "./ClientFactRow";
import DriveTimeButton from "../../adminComponents/DriveTime/DriveTimeButton";
import {
  makePlace,
  useDriveTimePins,
} from "../../../../utils/DriveTimePin/DriveTimePinContext";
import { useTourAction } from "../../../../tours/tourActions";
import { isTourActive } from "../../../../tours/tourRuntime";
import { clientSummary } from "./clientSummarySelectors";
import useNavHeightVar from "../../../../utils/useNavHeightVar";
import * as sectionSelect from "./clientSectionSelectors";
import "./singleClientConsole.css";
import { TONE_CLASS } from "../../../../components/BdrDisclosure/BdrDisclosure";
import BdrModal from "../../../../components/BdrModal/BdrModal";

const clientObjCleared = {
  clientId: "",
  firstName: "",
  lastName: "",
  spouseName: "",
  phoneNumberIds: [
    {
      phoneNumber: "",
      numberType: "",
      title: "",
      unreadMessages: false,
    },
  ],
  primaryEmail: "",
  secondaryEmail: "",
  streetAddress: "",
  city: "",
  state: "Texas",
  zipCode: "",
  gateCode: "",
  clientNotes: {
    note: "",
    dateCreated: "",
    dateUpdated: "",
    updatedBy: "",
    createdBy: "",
  },
  clientStatus: "Lead",
  quotes: [],
  clientPhotoIds: [],
};


const emailObjBlank = {
  type: "blank",
  // bigginsdoorrefinishing.com/client/view-quote/*quoteId
  // bigginsdoorrefinishing.com/client/view-invoice/*invoiceId
  quoteLink: [],
  invoiceLink: [],
  toFirstName: "",
  attachArr: [],
};

/*
 * Tile order, and therefore slide direction: moving to a higher index enters
 * from the right, a lower one from the left. Module-level so the reference is
 * stable — a fresh array each render is what stranded the VS blank.
 */
const SECTION_ORDER = [
  "messages",
  "emails",
  "notes",
  "photos",
  "invoices",
  "quotes",
  "tasks",
  "activity",
];

const SECTION_META = {
  messages: { label: "Messages", icon: <ForumIcon /> },
  emails: { label: "Emails", icon: <MailOutlineIcon /> },
  notes: { label: "Notes", icon: <StickyNote2Icon /> },
  photos: { label: "Photos", icon: <PhotoLibraryIcon /> },
  invoices: { label: "Invoices", icon: <ReceiptLongIcon /> },
  quotes: { label: "Quotes", icon: <RequestQuoteIcon /> },
  tasks: { label: "Tasks", icon: <TaskAltIcon /> },
  activity: { label: "Activity", icon: <MonitorHeartIcon /> },
};

const SingleClient = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  // Publishes --bdr-nav-h so .scc-page can subtract the nav from 100dvh.
  useNavHeightVar();

  /*
   * The selected tile lives in the URL, so a refresh keeps your place and a
   * link can point at a section. REPLACE, not push: eight tiles pushing history
   * would make Back walk tile-by-tile instead of returning to the Clients list,
   * which the breadcrumb implies is the way out.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionParam = searchParams.get("section");
  const activeSection = SECTION_ORDER.includes(sectionParam)
    ? sectionParam
    : SECTION_ORDER[0];

  const selectSection = useCallback(
    (key) => {
      const next = new URLSearchParams(searchParams);
      next.set("section", key);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  const { handleSnack, handleClose: handleSnackClose } = useSnackbar();
  const [getClientInfo] = useLazyQuery(GET_CLIENT_BY_ID, {
    fetchPolicy: "network-only",
  });
  const [getTextsSince] = useLazyQuery(GET_PHONE_TEXTS_SINCE, {
    fetchPolicy: "network-only",
  });
  const [deleteClientCall] = useMutation(DELETE_CLIENT, {
    fetchPolicy: "network-only",
  });
  const [updatePhnMsgsRead] = useMutation(UPDATE_PHN_MSGS_READ, {
    fetchPolicy: "network-only",
  });
  /* "web:drive-time-pins" walkthrough (registry §17.10): pin THIS customer
     as the demo start. The provider parks the user's real pins and restores
     them when the tour ends. Safe to run twice (Back re-runs it). */
  const pins = useDriveTimePins();
  useTourAction("drive-time-demo-pin-start", () => {
    if (!clientObj?._id) return;
    pins.beginDemo(
      makePlace(`${clientObj?.firstName || ""} ${clientObj?.lastName || ""}`.trim(), clientObj)
    );
  });
  const [bdrLoader, setBdrLoader] = useState(false);
  /* The edit surface. Both triggers — the rail's Edit icon and the referral
     fact button — open this one modal. */
  const [editModalOpen, setEditModalOpen] = useState(false);
  /* Console only. The rail's MANAGED BY row navigates when a manager is set and
     opens this when one is not — the link flow itself stays in
     ManagerLinkContainer rather than being re-implemented here. */
  const [managerModalOpen, setManagerModalOpen] = useState(false);
  /*
   * Emails is the one section whose numbers cannot come from the client query:
   * its mail is matched by ADDRESS across two collections rather than joined
   * by clientId, so it is not on the client document.
   *
   * Two things feed this, deliberately:
   *   on page load  `getClientEmailCounts` - counts only, no documents, so the
   *                 TILE can report before the section has ever been opened. A
   *                 badge that only appears after you have clicked the thing
   *                 it was meant to advise you about is useless.
   *   on open       ClientEmailDisplay reports up from the list it just
   *                 fetched, which supersedes the counts and keeps the tile
   *                 honest as flags and read states are changed in the panel.
   *
   * INVARIANT: both sources count INCOMING mail only. They are two different
   * implementations of one number, so if their definitions drift the tile and
   * the panel header openly contradict each other - which is how this was
   * found. The definitions live at `getClientEmailCounts` (resolvers.js) and
   * at the top of ClientEmailDisplay; change one, change both.
   */
  const [emailSummary, setEmailSummary] = useState(null);
  /*
   * Set when an Activity row is clicked for a record whose modal belongs to a
   * SECTION rather than to this page. Activity switches to that section and
   * hands it the id; the section opens its own modal. The alternative was
   * lifting three modals out of three components, which forks their state.
   *
   * `{ type, id, nonce }` — the nonce makes clicking the SAME row twice a new
   * value, so a modal the user closed can be reopened.
   */
  const [focusRecord, setFocusRecord] = useState(null);
  const [loaderMess, setLoaderMess] = useState("");

  // quote and invoice editing states
  const [openQuoteInvoiceEditModal, setOpenQuoteInvoiceEditModal] =
    useState(false);
  const [selectedToEdit, setSelectedToEdit] = useState({});
  const [selectedToEditIsInvoice, setSelectedToEditIsInvoice] = useState(false);

  const [selectedQuote, setSelectedQuote] = useState({});
  const [selectedInvoice, setSelectedInvoice] = useState("");
  const [selectedPhnId, setSelectedPhnId] = useState("");
  const [clickTrigger, setClickTrigger] = useState(0);
  const [openEmailModal, setOpenEmailModal] = useState(false);
  const [emailModalObj, setEmailModalObj] = useState({ ...emailObjBlank });
  /* Which address the composer opens against. The modal used to be hard-wired
     to `primaryEmail`; the rail's 2ND EMAIL row needs to reach the other one.
     Null means "primary", so every existing caller is unchanged. */
  const [emailSendTo, setEmailSendTo] = useState(null);

  const [clientObj, setClientObj] = useState({ ...clientObjCleared });

  // Refs so the once-registered SSE callbacks read CURRENT values (no stale
  // closure). clientObjRef carries this client's phone ids + held messages.
  const selectedPhnIdRef = useRef("");
  const clientObjRef = useRef(clientObj);
  useEffect(() => {
    selectedPhnIdRef.current = selectedPhnId;
  });
  useEffect(() => {
    clientObjRef.current = clientObj;
  }, [clientObj]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (clientId?.length === 24) {
      changeSaving(true, "Loading...");
      fetchClientInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    if (selectedInvoice?.id) {
      openInvoiceModal(selectedInvoice?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientObj]);

  useEffect(() => {
    if (selectedPhnId?.length > 0) {
      markMsgsRead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPhnId]);

  // 60s focused-only backstop poll — the guaranteed floor under SSE. Only
  // fires when a thread is open; refetches just that phone's messages.
  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedPhnIdRef.current) {
        appendNewMessages(selectedPhnIdRef.current);
      }
    }, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SSE: a new inbound text arrived for some number.
  //  - Focused thread (phoneNumId === selectedPhnId): incremental append.
  //  - Belongs to this client but not focused: reorder/dot only (no fetch).
  //  - Belongs to a different client: ignore.
  const handleSmsReceived = useCallback((data) => {
    if (!data?.phoneNumId) return;
    const current = clientObjRef.current;
    const owns = current?.phoneNumberIds?.some(
      (p) => p?._id === data.phoneNumId,
    );
    if (!owns) return;

    if (data.phoneNumId === selectedPhnIdRef.current) {
      appendNewMessages(data.phoneNumId);
      return;
    }

    // Not focused: raise the unread dot + bump recentTime from the payload.
    setClientObj((prev) => ({
      ...prev,
      phoneNumberIds: prev?.phoneNumberIds?.map((p) =>
        p?._id === data.phoneNumId
          ? {
              ...p,
              unreadMessages: true,
              recentTime: data.recentTime ?? p?.recentTime,
            }
          : p,
      ),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SSE reconnect/backfill: pull the focused thread's missed messages.
  const handleSseReconnected = useCallback(() => {
    if (selectedPhnIdRef.current) {
      appendNewMessages(selectedPhnIdRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useSSEListener("sms_received", handleSmsReceived);
  useSSEListener(SSE_RECONNECTED, handleSseReconnected);

  /* useCallback because renderSectionPanel depends on it — without a stable
     identity the VS's panel renderer changes on every render. */
  const closeShowTexts = useCallback((changed) => {
    setSelectedPhnId("");
    if (changed) {
      fetchClientInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * The Messages panel needs a thread. Selecting its tile without one (a direct
   * ?section=messages link, or clicking the tile rather than a phone number in
   * the rail) would otherwise show an empty conversation.
   *
   * Clearing it when you leave matters as much as setting it: `selectedPhnId`
   * drives the 60s backstop poll and the SSE focused-thread branch, both of
   * which mark a thread read server-side. Leaving it set while the user reads
   * Invoices means inbound texts get marked read for a conversation nobody is
   * looking at.
   */
  /*
   * Gate on whether a thread is ACTUALLY RENDERED, not on the selected tile.
   *
   * Keying this on `activeSection` alone once left a hole: the old inline Edit
   * branch did not change the section, and "messages" is the default, so
   * clicking Edit Client unmounted the console (and the thread with it) while
   * `selectedPhnId` stayed set. The 60s poll and the SSE focused-thread branch then kept calling
   * `appendNewMessages`, whose since-query clears unread SERVER-SIDE and
   * broadcasts `sms_read`. A text arriving while someone sat in the edit form
   * was marked read with nothing on screen.
   *
   * That hole closed with the Edit branch (2026-10-02): editing is a MODAL
   * now and does not unmount the console, so the thread stays rendered and
   * marking it read is legitimate. The condition is simply which section is
   * open.
   */
  const threadVisible = activeSection === "messages";

  useEffect(() => {
    if (threadVisible) {
      if (!selectedPhnId && clientObj?.phoneNumberIds?.[0]?._id) {
        setSelectedPhnId(clientObj.phoneNumberIds[0]._id);
      }
      return;
    }

    if (selectedPhnId) {
      setSelectedPhnId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadVisible, clientObj?.phoneNumberIds, selectedPhnId]);

  const quotesDisplayTrigger = () => {
    setClickTrigger((prev) => prev + 1);
  };

  /* useCallback: `renderSectionPanel` passes this into the Quotes panel, so a
     fresh function each render would churn the whole VS on every render. */
  const openQuoteModal = useCallback(
    (quoteId) => {
      if (quoteId === "") {
        setSelectedQuote({});
        return;
      }

      const selected = clientObj?.quotes?.filter((quote) => {
        return quote?.id === quoteId ? quote : null;
      })[0];

      if (selected) {
        setSelectedQuote({ ...selected });
      }
    },
    [clientObj?.quotes],
  );

  /* useCallback for the same reason as openQuoteModal. */
  const openInvoiceModal = useCallback(
    (invoiceId) => {
      if (invoiceId === "") {
        setSelectedInvoice({});
        return;
      }

      const selected = clientObj?.invoices?.filter((invoice) => {
        return invoice?.id === invoiceId ? invoice : null;
      })[0];

      if (selected) {
        setSelectedInvoice({ ...selected });
      }
    },
    [clientObj?.invoices],
  );

  const changeSaving = (bool, mes) => {
    setLoaderMess(mes);
    setBdrLoader(bool);
  };

  const markMsgsRead = async () => {
    clientObj?.phoneNumberIds?.forEach(async (item) => {
      if (item?._id === selectedPhnId) {
        if (item?.unreadMessages === true) {
          const options = {
            phnId: selectedPhnId,
            localEpoch: Date.now().toString(),
          };

          // Tour page opt-out (guide §6): a walkthrough visiting this page
          // must not mark a real thread read.
          if (isTourActive()) return;
          const { data } = await updatePhnMsgsRead({
            variables: options,
          });

          if (data?.updatePhnMsgsRead) {
            setClientObj((prev) => {
              const updatedPhn = prev?.phoneNumberIds?.map((item) => {
                if (item?._id === selectedPhnId) {
                  return { ...item, unreadMessages: false };
                }
                return item;
              });

              return { ...prev, phoneNumberIds: updatedPhn };
            });
          }
        }
      }
    });
  };

  // Newest server-clock sentReceivedTime currently held for one phone id.
  // Rows without a server _id are excluded — client-clock rows must never
  // advance the incremental cursor.
  const newestHeldTimeForPhone = (phnId) => {
    const phn = clientObjRef.current?.phoneNumberIds?.find(
      (p) => p?._id === phnId,
    );
    let max = 0;
    phn?.textMessages?.forEach((m) => {
      if (!m?._id) return;
      const t = parseInt(m?.sentReceivedTime);
      if (!Number.isNaN(t) && t > max) max = t;
    });
    return max.toString();
  };

  // Incremental fetch of only messages newer than what we hold for `phnId`,
  // merged + deduped by subdoc _id. Used by SSE focused-thread updates, the
  // 60s backstop, and reconnect backfill. Marks the thread read (default
  // since-query behavior).
  const appendNewMessages = async (phnId) => {
    if (!phnId) return;
    const since = newestHeldTimeForPhone(phnId);
    const { data } = await getTextsSince({
      variables: {
        phoneId: phnId,
        sinceEpoch: since,
        localEpoch: Date.now().toString(),
      },
    });

    const incoming = data?.getPhoneNumTextsSince?.textMessages;
    if (!incoming || incoming.length === 0) return;

    setClientObj((prev) => {
      const updatedPhn = prev?.phoneNumberIds?.map((item) => {
        if (item?._id !== phnId) return item;

        const existing = Array.isArray(item?.textMessages)
          ? [...item.textMessages]
          : [];
        const seenIds = new Set(
          existing.filter((m) => m?._id).map((m) => m._id),
        );
        const toAppend = incoming.filter((m) => !m?._id || !seenIds.has(m._id));

        const merged = [...existing, ...toAppend].sort((a, b) =>
          parseInt(a?.sentReceivedTime) > parseInt(b?.sentReceivedTime)
            ? 1
            : -1,
        );

        return {
          ...item,
          textMessages: merged,
          recentTime:
            data?.getPhoneNumTextsSince?.recentTime ?? item?.recentTime,
          // Focused thread is read; the since-query marks-read server-side.
          unreadMessages:
            phnId === selectedPhnIdRef.current ? false : item?.unreadMessages,
        };
      });

      return { ...prev, phoneNumberIds: updatedPhn };
    });
  };

  /*
   * `handlePageHeight` lived here and has been removed. It measured
   * #single-client-page-hdr-marker, WHICH DOES NOT EXIST ANYWHERE in the repo —
   * so `totalHeight` was NaN, `minHeight: "NaNpx"` was rejected by CSSOM, and
   * the page silently fell back to `auto` one second after every load. It had
   * been inert for as long as that marker has been missing.
   *
   * The same pattern is alive and WORKING on five other pages (Clients, Metrics,
   * Messages, ImageGroups, StripePayment — 93 references across 21 files). Only
   * this page's broken copy is gone; do not grep `heightTrigger`, find those,
   * and assume the deletion was half-finished.
   */

  const fetchClientInfo = async () => {
    const { data } = await getClientInfo({
      // activeTasksOnly — completed tasks are lazy-loaded inside
      // ClientTasksDisplay via GET_CLIENT_COMPLETED_TASKS.
      variables: {
        clientId,
        currentTime: Date.now().toString(),
        activeTasksOnly: true,
      },
      fetchPolicy: "network-only",
    });
    if (data?.getClientById) {
      // sorting newest first
      const sortedQuotes = [...data?.getClientById?.quotes]?.sort(
        (quote1, quote2) => {
          return parseInt(quote2?.dateCreated) - parseInt(quote1?.dateCreated);
        },
      );

      const filteredDataPrev = data?.getClientById?.phoneNumberIds?.map(
        (obj) => {
          return {
            numberType: obj?.numberType,
            phoneNumber: obj?.phoneNumber,
            _id: obj?._id,
            title: obj?.title,
            recentTime: obj?.recentTime,
            textConsent: obj?.textConsent,
            textMessages: obj?.textMessages,
            unreadMessages: obj?.unreadMessages,
          };
        },
      );


      setClientObj({
        ...data?.getClientById,
        phoneNumberIds: filteredDataPrev,
        quotes: [...sortedQuotes],
      });
      /* The edit form's own seed (and its `|| ""` coercion for lastName /
         primaryEmail) moved into ClientFormModal, which seeds from `clientObj`
         on the open transition. Seeding it from here as well would be the
         re-seed-while-open hazard the plan rules out. */
    }

    changeSaving(false, "");
    return;
  };

  const handleUpdatedTasksArr = (taskUpdated) => {
    if (taskUpdated) {
      const updatedArr = clientObj?.clientTasks?.map((task) => {
        if (task?.id === taskUpdated?.id) {
          return taskUpdated;
        }
        return task;
      });

      setClientObj((prev) => ({
        ...prev,
        clientTasks: [...updatedArr],
      }));
    }
  };


  const addPhotoIdToClient = (id) => {
    setClientObj((prev) => ({
      ...prev,
      clientPhotoIds: [...prev?.clientPhotoIds, id],
    }));
  };

  const removePhotoIdFromClient = (id) => {
    if (!id) return;

    setClientObj((prev) => ({
      ...prev,
      clientPhotoIds: (prev?.clientPhotoIds || []).filter((x) => x !== id),
    }));

  };

  const changeParentForEmailModal = (bool) => {
    setOpenEmailModal(bool);
    // Drop back to the primary address once the composer closes, so the next
    // opener does not silently inherit whichever row was clicked last.
    if (!bool) setEmailSendTo(null);
  };

  /*
   * Open the composer addressed to one specific address. Used by the rail's
   * EMAIL and 2ND EMAIL rows.
   *
   * The template is reset to blank first: `emailModalObj` is shared with the
   * quote/invoice senders, so without this a composer opened straight after
   * "email this invoice" would still be carrying that invoice's link and
   * attachments.
   */
  const composeTo = (address) => {
    if (!address) return;
    emailObjHandler({ ...emailObjBlank });
    setEmailSendTo(address);
    setOpenEmailModal(true);
  };

  /* useCallback: shared by both the Invoices and Quotes panels. */
  const openEditInvoiceQuoteModal = useCallback(
    (isInvoiceBool, objId) => {
      const selected = isInvoiceBool
        ? clientObj?.invoices?.filter((invoice) => {
            return invoice?.id === objId ? invoice : null;
          })[0]
        : clientObj?.quotes?.filter((quote) => {
            return quote?.id === objId ? quote : null;
          })[0];

      if (selected) {
        setOpenQuoteInvoiceEditModal(true);
        setSelectedToEdit(selected);
        setSelectedToEditIsInvoice(isInvoiceBool);
      }
    },
    [clientObj?.invoices, clientObj?.quotes],
  );

  const updateInvoiceQuote = (obj, isInvoiceBool) => {
    if (isInvoiceBool) {
      const updatedArr = clientObj?.invoices?.map((invoice) => {
        if (obj?.id === invoice?.id) {
          return obj;
        }
        return invoice;
      });

      setClientObj((prev) => ({
        ...prev,
        invoices: [...updatedArr],
      }));
    }

    if (!isInvoiceBool) {
      const updatedArr = clientObj?.quotes?.map((quote) => {
        if (obj?.id === quote?.id) {
          return obj;
        }
        return quote;
      });

      setClientObj((prev) => ({
        ...prev,
        quotes: [...updatedArr],
      }));
    }

    closeEditInvoiceQuoteModal();
  };

  const closeEditInvoiceQuoteModal = () => {
    setOpenQuoteInvoiceEditModal(false);
    setSelectedToEdit({});
  };


  const deleteClientHandler = async () => {
    if (!clientObj?._id) {
      handleSnackClose();
      handleSnack(
        "Error attempting to delete client...Please refresh and try again",
        4000,
      );
      return;
    }

    try {
      const { data } = await deleteClientCall({
        variables: {
          clientId: clientObj?._id,
        },
      });

      if (data?.deleteClient) {
        /* Tasks, not the Clients page — that page no longer exists (it became
           the navbar's Clients dropdown on 2026-10-05). Owner's choice of
           destination. */
        navigate(`/bdr-office/tasks`);
        return;
      }

      handleSnack(
        "Error trying to delete client...Please refresh and try again",
        4000,
      );
    } catch (err) {
      // Surfaces server-side cascade-block (deleteClient throws when this
      // client manages others) so admin sees the explicit reason instead
      // of a silent failure.
      const msg = err?.message || "Error trying to delete client";
      handleSnack(msg, 6000);
    }
  };

  /* Stable identity: ClientEmailDisplay reports through this from an effect, so
     a fresh function each render would re-fire it every render. Compares before
     setting, so an unchanged report costs no re-render either. */
  const [getEmailCounts] = useLazyQuery(GET_CLIENT_EMAIL_COUNTS, {
    fetchPolicy: "network-only",
  });

  /*
   * Fetch the tile's email numbers as soon as the client's addresses are
   * known. Keyed on the ADDRESSES rather than the client object, so the 60s
   * poll and every SSE tick do not re-run it — only a change to who we are
   * counting mail for does.
   *
   * It never clears what it has: if the count query fails it returns zeroes
   * rather than throwing, and a reply that arrives AFTER the panel has
   * reported its own fresher numbers must not overwrite them.
   */
  const emailAddressKey = [clientObj?.primaryEmail, clientObj?.secondaryEmail]
    .filter(Boolean)
    .join("|");

  /*
   * Which source last wrote the numbers.
   *
   * Once the panel has reported, it OWNS them: it holds the actual list and
   * sees a flag change or a read the moment it happens, while this query is a
   * page-load snapshot. But the guard has to be "has the panel spoken", not
   * "is anything set" - keying it on `emailSummary` being truthy would also
   * block a legitimate re-count after the client's address was edited, which
   * is exactly when the old numbers are wrong.
   */
  const panelOwnsCountsRef = useRef(false);

  useEffect(() => {
    if (!emailAddressKey) return undefined;
    let cancelled = false;

    getEmailCounts({ variables: { clientEmails: emailAddressKey.split("|") } })
      .then(({ data }) => {
        const counts = data?.getClientEmailCounts;
        if (cancelled || !counts) return;
        if (panelOwnsCountsRef.current) return;
        setEmailSummary(counts);
      })
      .catch(() => {
        /* A tile badge is not worth surfacing an error for; the panel will
           supply real numbers the moment it is opened. */
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailAddressKey]);

  const handleEmailSummary = useCallback((next) => {
    panelOwnsCountsRef.current = true;
    setEmailSummary((prev) =>
      prev?.unread === next?.unread &&
      prev?.flagged === next?.flagged &&
      prev?.urgent === next?.urgent
        ? prev
        : next,
    );
  }, []);

  /*
   * An Activity row was clicked. Quotes and invoices have their modals on this
   * page already; everything else is handed to its section.
   */
  const openActivityRecord = useCallback(
    (refType, refId) => {
      if (!refType || !refId) return;
      if (refType === "quote") {
        openQuoteModal(refId);
        return;
      }
      if (refType === "invoice") {
        openInvoiceModal(refId);
        return;
      }
      if (refType === "jobform") {
        navigate("/bdr-office/job-form-review");
        return;
      }
      const section = { note: "notes", photo: "photos", task: "tasks" }[refType];
      if (!section) return;
      setFocusRecord({ type: refType, id: refId, nonce: Date.now() });
      selectSection(section);
    },
    [openQuoteModal, openInvoiceModal, navigate, selectSection]
  );

  /*
   * A focus record is a ONE-SHOT instruction, and it has to be cleared by the
   * section that carries it out — this page cannot know when that happened.
   *
   * Left set, it outlives the trip it was made for: the section unmounts when
   * you switch tiles and MOUNTS AGAIN when you come back, its focus effect
   * re-runs against the same still-set record, and a photo you opened from
   * Activity and closed minutes ago springs open again on the next visit to
   * the tile. The `nonce` only makes the same row re-clickable; it does not
   * expire the record.
   */
  const clearFocusRecord = useCallback(() => setFocusRecord(null), []);

  const emailObjHandler = ({
    type,
    quoteLink,
    invoiceLink,
    toFirstName,
    attachArr,
  }) => {
    // bigginsdoorrefinishing.com/client/view-quote/*quoteId
    // bigginsdoorrefinishing.com/client/view-invoice/*invoiceId
    // const multiQuotes = quoteLink ? [...quoteLink] : []
    setEmailModalObj({
      type,
      quoteLink: quoteLink ? [...quoteLink] : [],
      invoiceLink: invoiceLink || "",
      toFirstName: toFirstName || clientObj?.firstName,
      attachArr: attachArr || [],
    });
    return type;
  };

  /*
   * Tile counts and statuses come from a SHARED selector module, not from the
   * section components — those only mount when their tile is selected, but the
   * tile has to show a count before that. Computing them inline here instead
   * would fork four non-trivial derivations, and "what counts as an active
   * quote" living in two places is a drift bug with a date on it.
   *
   * Emails is deliberately `undefined`: its list is fetched lazily and carries
   * year-divider pseudo-rows a raw length would over-count, so it shows no
   * badge until the section has been opened once. `undefined` is not `0` —
   * zero would claim the client has no emails.
   */
  /*
   * "Since" = the year of their FIRST INVOICE. Not the year the client record
   * was created, which is what this used to read out of the ObjectId.
   *
   * WHY, and do not "fix" this back. The business has operated since 2012 but
   * ran on other systems first, so a large share of clients were IMPORTED —
   * their client record was created in the last ~2.5 years while their actual
   * work with us goes back a decade. The record's creation date is therefore
   * wrong for exactly the clients the number matters most for. Their imported
   * JOBS carry the real dates, so the first invoice is the honest answer.
   *
   * `jobScheduledFor` before `dateCreated`: for an imported job the scheduled
   * date is the original work date, while `dateCreated` can be the date of the
   * import run. Min across every invoice, because import order is not date
   * order. Both fields are epoch-ms STRINGS (project convention).
   *
   * Returns null when there are no invoices — a client who has never been
   * invoiced has no "since", and inventing one from the record's creation date
   * is the exact bug this replaced.
   */
  const clientSince = useMemo(() => {
    const invoices = clientObj?.invoices;
    if (!Array.isArray(invoices) || invoices.length === 0) return null;

    let earliest = null;
    invoices.forEach((invoice) => {
      const raw = invoice?.jobScheduledFor || invoice?.dateCreated;
      const ms = Number(raw);
      if (!Number.isFinite(ms) || ms <= 0) return;
      if (earliest === null || ms < earliest) earliest = ms;
    });

    if (earliest === null) return null;
    return new Date(earliest).getFullYear();
  }, [clientObj?.invoices]);

  const consoleTiles = useMemo(() => {
    const data = {
      messages: sectionSelect.messageTile(clientObj?.phoneNumberIds),
      emails: sectionSelect.emailTile(emailSummary),
      notes: sectionSelect.noteTile(clientObj?.clientNotes),
      photos: sectionSelect.photoTile(clientObj?.clientPhotoIds),
      invoices: sectionSelect.invoiceTile(clientObj?.invoices),
      quotes: sectionSelect.quoteTile(clientObj?.quotes),
      tasks: sectionSelect.taskTile(clientObj?.clientTasks),
      activity: sectionSelect.activityTile(),
    };

    return SECTION_ORDER.map((key) => ({
      key,
      label: SECTION_META[key].label,
      icon: SECTION_META[key].icon,
      ...data[key],
    }));
  }, [clientObj, emailSummary]);

  /*
   * key -> statuses, so the VS header can spell out what its tile abbreviates.
   * The tile has room for `value` (the bare number); the header shows `text`
   * (the phrase that makes the number mean anything). One source, so the two
   * cannot drift apart. An ARRAY because Emails reports up to three at once.
   */
  /* The handful of numbers the console could never show without adding up a
     list by eye. All derived from the client document already loaded. */
  const summary = useMemo(() => clientSummary(clientObj), [clientObj]);

  const sectionStatus = useMemo(() => {
    const map = {};
    consoleTiles.forEach((tile) => {
      if (tile.statuses?.length) map[tile.key] = tile.statuses;
    });
    return map;
  }, [consoleTiles]);

  /*
   * Every section but Activity now renders its real component here, each with
   * `inPanel`: no BdrDisclosure wrapper, always open, and no document-scroll
   * behaviour, since the console's page is viewport-locked and the PANEL
   * scrolls. Each of those components still renders its ORIGINAL collapsible
   * form on the Edit branch — `inPanel` is the only difference, so there is
   * one implementation of every section, not two.
   *
   * Activity alone still shows a placeholder, because it has no resolver.
   */
  /*
   * Permanently stable identities for the handlers the VS panels receive.
   *
   * `renderSectionPanel` is a useCallback, and anything in its dep array that
   * is rebuilt each render churns it — which re-renders the whole VS on every
   * parent render. Five of these handlers are plain function declarations, and
   * several are load-bearing elsewhere. (This used to say `fetchClientInfo` is
   * called by the 60s poll and both SSE branches — it is NOT: the poll calls
   * `appendNewMessages` and SSE calls `setClientObj`. Corrected 2026-10-02;
   * the identity argument below stands either way.)
   * Rewriting those into useCallbacks would mean
   * getting five dependency arrays right on code that already works, to fix a
   * problem that is only about identity.
   *
   * So the functions are left exactly as they are and wrapped once. The ref
   * always holds the latest version, and the wrapper never changes identity —
   * the `useEventCallback` pattern. Reading through a ref is what makes it
   * safe: the wrapper cannot capture a stale closure.
   */
  const latestHandlers = useRef({});
  latestHandlers.current = {
    changeSaving,
    fetchClientInfo,
    handleUpdatedTasksArr,
    addPhotoIdToClient,
    removePhotoIdFromClient,
  };
  const panelHandlers = useRef({
    changeSaving: (...args) => latestHandlers.current.changeSaving(...args),
    fetchClientInfo: (...args) =>
      latestHandlers.current.fetchClientInfo(...args),
    handleUpdatedTasksArr: (...args) =>
      latestHandlers.current.handleUpdatedTasksArr(...args),
    addPhotoIdToClient: (...args) =>
      latestHandlers.current.addPhotoIdToClient(...args),
    removePhotoIdFromClient: (...args) =>
      latestHandlers.current.removePhotoIdFromClient(...args),
  }).current;

  const renderSectionPanel = useCallback(
    (key) => {
      const meta = SECTION_META[key];
      const statuses = sectionStatus[key] || [];

      return (
        <ClientConsolePanelShell
          meta={meta}
          statuses={statuses}
          toneClass={TONE_CLASS}
          fillBody={key === "messages"}
        >
            {key === "messages" ? (
              /*
               * Messages is REAL in Phase 1, not a placeholder, and it is the
               * only section that is. Two reasons it could come early: it never
               * had a BdrDisclosure wrapper to strip (every sweep missed it) and
               * it has no lazy query — it renders its full tree from props.
               *
               * It also had to. Before this, the rail's phone buttons set
               * `selectedPhnId`, which marks that thread read SERVER-SIDE, while
               * the panel showed a placeholder: the unread dot cleared for a
               * conversation nobody had opened. And the thread UI was otherwise
               * reachable only by entering Edit mode. Its own header and Close
               * button are hidden here via `hideHeader` — left visible, Close
               * did not merely look redundant, it cleared selectedPhnId and the
               * console re-selected phone 1, silently switching a two-number
               * client's thread.
               */
              <ClientTextMesDisplay
                textMesIncArr={clientObj?.phoneNumberIds}
                closeMessages={closeShowTexts}
                selectedPhnId={selectedPhnId}
                hideHeader
              />
            ) : key === "activity" ? (
              <ClientActivityDisplay
                clientId={clientId}
                onOpenRecord={openActivityRecord}
              />
            ) : key === "notes" ? (
              <ClientNotesDisplay
                changeSaving={panelHandlers.changeSaving}
                notesArr={clientObj?.clientNotes}
                clientId={clientId}
                queryClient={panelHandlers.fetchClientInfo}
                gateCode={clientObj?.gateCode}
                invoicesArr={clientObj?.invoices}
                phnNumArr={clientObj?.phoneNumberIds}
                focusRecord={focusRecord?.type === "note" ? focusRecord : null}
                onFocusHandled={clearFocusRecord}
                inPanel
              />
            ) : key === "photos" ? (
              <ClientPicsDisplay
                photoIdsArr={clientObj?.clientPhotoIds}
                clientId={clientObj?._id}
                addPhotoIdToClient={panelHandlers.addPhotoIdToClient}
                removePhotoIdFromClient={panelHandlers.removePhotoIdFromClient}
                focusRecord={focusRecord?.type === "photo" ? focusRecord : null}
                onFocusHandled={clearFocusRecord}
                inPanel
              />
            ) : key === "tasks" ? (
              <ClientTasksDisplay
                incTasks={clientObj?.clientTasks}
                addUpdatedTaskToArr={panelHandlers.handleUpdatedTasksArr}
                refetch={panelHandlers.fetchClientInfo}
                clientId={clientId}
                focusRecord={focusRecord?.type === "task" ? focusRecord : null}
                onFocusHandled={clearFocusRecord}
                inPanel
              />
            ) : key === "invoices" ? (
              /*
               * `barCollapsed` is left off on purpose — it is the Edit
               * branch's parent-owned collapse state, and `inPanel` ignores it
               * entirely. Passing it would suggest the panel can be collapsed,
               * which it cannot: the panel IS the open state.
               */
              <ClientInvoicesDisplay
                invoicesArr={clientObj?.invoices}
                openInvoiceModal={openInvoiceModal}
                openEditInvoiceQuoteModal={openEditInvoiceQuoteModal}
                inPanel
              />
            ) : key === "quotes" ? (
              <ClientQuotesDisplay
                quotesArr={clientObj?.quotes}
                openQuoteModal={openQuoteModal}
                clickTrigger={clickTrigger}
                openEditInvoiceQuoteModal={openEditInvoiceQuoteModal}
                inPanel
              />
            ) : key === "emails" ? (
              /*
               * Phase 2, first section moved in. It renders with `inPanel`,
               * which drops its BdrDisclosure wrapper (the panel header already
               * names the section) and its document-scroll behaviour, which
               * belongs to a page that scrolls — this one does not, the panel
               * does. `onSummary` is how the tile finally gets a count: this
               * list is fetched lazily, so nothing upstream can know it.
               */
              <ClientEmailDisplay
                clientEmails={[
                  clientObj?.primaryEmail,
                  clientObj?.secondaryEmail,
                ]}
                inPanel
                onSummary={handleEmailSummary}
              />
            ) : (
              /* Activity is the only key left that reaches this. Every other
                 section now renders its real component above, so the
                 "moves here next" copy that used to live here is gone. */
              <div className="scc-vs-empty">
                <strong>Coming Soon</strong>
                <span>The cross-section activity feed needs its resolver.</span>
              </div>
            )}
        </ClientConsolePanelShell>
      );
    },
    [
      clearFocusRecord,
      clientObj?.phoneNumberIds,
      clientObj?.primaryEmail,
      clientObj?.secondaryEmail,
      clientObj?.invoices,
      clientObj?.quotes,
      clientObj?.clientNotes,
      clientObj?.clientPhotoIds,
      clientObj?.clientTasks,
      focusRecord,
      openActivityRecord,
      clientObj?.gateCode,
      clientObj?._id,
      clientId,
      panelHandlers,
      selectedPhnId,
      closeShowTexts,
      sectionStatus,
      handleEmailSummary,
      openInvoiceModal,
      openQuoteModal,
      openEditInvoiceQuoteModal,
      clickTrigger,
    ],
  );

  return (
    /*
     * ONE MODE. The transitional `editClient` branch — a second, older copy of
     * this whole page that existed only to host the 26-field form — was
     * deleted 2026-10-02 once that form became `ClientFormModal`.
     *
     * The old `minHeight: NaNpx` is gone: #single-client-page-hdr-marker never
     * existed, so handlePageHeight had been producing NaN and the browser had
     * been discarding it.
     */
    <div
      className="scc-page"
      id="single-client-height-marker"
    >
      {bdrLoader && <AdminBDRSpinner message={loaderMess} />}
      {openEmailModal && (
        <SendEmailModal
          sendTo={emailSendTo || clientObj?.primaryEmail}
          templateObj={emailModalObj}
          changeParent={changeParentForEmailModal}
        />
      )}
      {openQuoteInvoiceEditModal && (
        <QuoteInvoiceEditModal
          selectedObj={selectedToEdit}
          isInvoice={selectedToEditIsInvoice}
          openBool={openQuoteInvoiceEditModal}
          closeModal={closeEditInvoiceQuoteModal}
          returnNewObj={updateInvoiceQuote}
        />
      )}
      {/*
        * Edit Client. Mounted unconditionally and gated on `open` so BdrModal
        * owns its own mount/unmount — and so `client` is only ever READ on the
        * open transition (§4.4). It must NOT be keyed on `clientObj`: the 60s
        * poll and SSE both replace that object, and a re-seed mid-edit would
        * wipe what the user is typing.
        */}
      <ClientFormModal
        open={editModalOpen}
        mode="edit"
        client={clientObj}
        onClose={() => setEditModalOpen(false)}
        onUpdated={() => {
          setEditModalOpen(false);
          fetchClientInfo();
        }}
        /* Delete moved here when the Edit branch was deleted — the console's
           rail comment had always said this modal was its destination. */
        onDelete={deleteClientHandler}
      />
      {selectedQuote?.id?.length === 24 && (
        <ViewClientQuoteModal
          selectedQuote={selectedQuote}
          closeQuoteModal={openQuoteModal}
          queryClient={fetchClientInfo}
          emailObjHandler={emailObjHandler}
          changeParentForEmailModal={changeParentForEmailModal}
        />
      )}
      {selectedInvoice?.id?.length === 24 && (
        <ViewClientInvoiceModal
          selectedInvoice={selectedInvoice}
          closeInvoiceModal={openInvoiceModal}
          queryClient={fetchClientInfo}
          emailObjHandler={emailObjHandler}
          changeParentForEmailModal={changeParentForEmailModal}
        />
      )}
      {clientObj?.firstName?.length > 0 && (
            <div className="scc-body">
              <aside className="scc-rail">
                {/* No avatar circle and no breadcrumb: the app has no client
                    photos, so a big disc of initials spent a lot of rail on
                    repeating the name printed directly under it, and the
                    breadcrumb repeated it a third time. */}
                <div className="scc-rail-identity">
                  <h2 className="scc-rail-name">
                    {clientObj?.firstName} {clientObj?.lastName}
                  </h2>
                  {/* Badge and "since" share ONE row. They were stacked, which
                      read as the word "Client" twice in a column and cost a
                      whole row of the rail's fixed height. */}
                  <span className="scc-rail-status-row">
                    {clientObj?.clientStatus && (
                      <span className="bdr-badge bdr-badge--accent">
                        {clientObj?.clientStatus}
                      </span>
                    )}
                    {clientObj?.clientStatus && clientSince && (
                      <span className="scc-rail-sep" aria-hidden="true">
                        -
                      </span>
                    )}
                    {clientSince && (
                      <span className="scc-rail-since">
                        Since {clientSince}
                      </span>
                    )}
                  </span>
                </div>

                <div className="scc-rail-divider" />

                {/* Three equal-width ICON buttons, not a stack of labelled
                    ones. The rail is a fixed-height column and the labelled
                    stack pushed the client's own details into a scroll at 100%
                    zoom — the actions were taking more room than the
                    information they act on. Delete is NOT here: it stays
                    alone at the bottom so a destructive action is never one
                    mis-click from an edit. */}
                <div className="scc-rail-quick">
                  <BdrButton
                    variant="action"
                    icon={<EditIcon />}
                    iconOnly
                    ariaLabel="Edit Client"
                    title="Edit Client"
                    onClick={() => setEditModalOpen(true)}
                  />
                  <CreateQuoteModal
                    clientObj={clientObj}
                    fetchClientInfo={fetchClientInfo}
                    quotesDisplayTrigger={quotesDisplayTrigger}
                    iconOnly
                  />
                  <UserTaskCreate
                    clientObj={clientObj}
                    queryTasks={fetchClientInfo}
                    iconOnly
                  />
                  {/* Drive time (registry §17.10): pin this client as a start
                      or end, or compare them against the pins. A fourth icon
                      in the row rather than a glyph in the address box, which
                      already holds three. */}
                  <span data-tour="client-drive-time">
                    <DriveTimeButton
                      place={makePlace(
                        `${clientObj?.firstName || ""} ${clientObj?.lastName || ""}`.trim(),
                        clientObj
                      )}
                      title="Drive time"
                    />
                  </span>
                </div>

                <div className="scc-facts">
                  {/* Phone numbers stay ACTIONABLE in the read-only rail: clicking
                    one selects the Messages tile AND that thread. Without it a
                    client with Home + Cell would be stuck on whichever thread
                    the tile defaults to — the rail is the only place that
                    switcher has ever lived. The Messages panel gets its own
                    picker too (owner: both). */}
                  {/* Phones, emails and the address are ClientFactRows: the
                    icon box COPIES (hover it and it turns into a copy glyph;
                    on a phone a tap on it copies), the body OPENS. The copy
                    buttons the pre-console page had were lost in the V2
                    rebuild; this is them, back, on every row they fit. */}
                  {clientObj?.phoneNumberIds?.map((phn) => {
                    const shown = phoneNumberFormat(phn?.phoneNumber || "");
                    return (
                      <ClientFactRow
                        key={phn?._id}
                        icon={<PhoneIcon />}
                        label={phn?.numberType || "PHONE"}
                        copyText={shown}
                        copyLabel="phone number"
                        openLabel={`Open Messages for ${shown}`}
                        onOpen={() => {
                          setSelectedPhnId(phn?._id);
                          selectSection("messages");
                        }}
                      >
                        {shown}
                      </ClientFactRow>
                    );
                  })}

                  {clientObj?.primaryEmail && (
                    <ClientFactRow
                      icon={<EmailIcon />}
                      label="EMAIL"
                      copyText={clientObj?.primaryEmail}
                      copyLabel="email"
                      title={`Email ${clientObj?.primaryEmail}`}
                      openLabel={`Email ${clientObj?.primaryEmail}`}
                      onOpen={() => composeTo(clientObj?.primaryEmail)}
                    >
                      {clientObj?.primaryEmail}
                    </ClientFactRow>
                  )}

                  {clientObj?.secondaryEmail && (
                    <ClientFactRow
                      icon={<AlternateEmailIcon />}
                      label="2ND EMAIL"
                      copyText={clientObj?.secondaryEmail}
                      copyLabel="email"
                      title={`Email ${clientObj?.secondaryEmail}`}
                      openLabel={`Email ${clientObj?.secondaryEmail}`}
                      onOpen={() => composeTo(clientObj?.secondaryEmail)}
                    >
                      {clientObj?.secondaryEmail}
                    </ClientFactRow>
                  )}

                  {/* No panel to open for an address, so the body is plain
                    text; the icon still copies the one-line form. */}
                  {clientObj?.streetAddress && (
                    <ClientFactRow
                      icon={<PlaceIcon />}
                      label="LOCATION"
                      copyText={addressOneLine(clientObj)}
                      copyLabel="address"
                    >
                      {clientObj?.streetAddress}
                      <br />
                      {clientObj?.city}, {clientObj?.state}{" "}
                      {clientObj?.zipCode}
                    </ClientFactRow>
                  )}

                  {clientObj?.gateCode && (
                    <div className="scc-fact">
                      <span className="scc-fact-icon">
                        <VpnKeyIcon />
                      </span>
                      <span className="scc-fact-body">
                        <span className="bdr-field-label">GATE CODE</span>
                        <span className="scc-fact-value">
                          {clientObj?.gateCode}
                        </span>
                      </span>
                    </div>
                  )}

                  {clientObj?.spouseName && (
                    <div className="scc-fact">
                      <span className="scc-fact-icon">
                        <PersonIcon />
                      </span>
                      <span className="scc-fact-body">
                        <span className="bdr-field-label">SPOUSE</span>
                        <span className="scc-fact-value">
                          {clientObj?.spouseName}
                        </span>
                      </span>
                    </div>
                  )}

                  {/* `referredBy.source` is the denormalised label. The report
                    side groups on `sourceOptionId`, never on this string — see
                    the referral-source normalisation work — but for DISPLAY the
                    stored label is exactly right and needs no lookup. */}
                  {clientObj?.referredBy?.source ? (
                    <div className="scc-fact">
                      <span className="scc-fact-icon">
                        <CampaignIcon />
                      </span>
                      <span className="scc-fact-body">
                        <span className="bdr-field-label">HEARD ABOUT US</span>
                        <span className="scc-fact-value">
                          {clientObj?.referredBy?.source}
                        </span>
                      </span>
                    </div>
                  ) : (
                    /* Kept visible when unset, because a MISSING referral source
                     is itself worth seeing — it is the field the marketing
                     spend reports group on. Repointed at the client modal
                     2026-10-02, as the comment here used to promise. This is
                     the SECOND edit trigger; the rail's Edit icon is the
                     other, and both must open the modal (plan §5 P3). */
                    <button
                      type="button"
                      className="scc-fact scc-fact--button"
                      title="Set where this client heard about us"
                      onClick={() => setEditModalOpen(true)}
                    >
                      <span className="scc-fact-icon">
                        <CampaignIcon />
                      </span>
                      <span className="scc-fact-body">
                        <span className="bdr-field-label">HEARD ABOUT US</span>
                        <span className="scc-fact-value scc-fact-value--empty">
                          Add referral source
                        </span>
                      </span>
                    </button>
                  )}

                  {/* Managed by is a fact AND a link — clicking it opens the
                    manager's own page, the same navigate ManagerLinkContainer
                    does. CHANGING or unlinking the manager is not here: that is
                    editing, and it goes with the 26-field form into the
                    edit/create modal. Until then the full control still lives
                    in the Edit branch. */}
                  {clientObj?.manager?._id ? (
                    <button
                      type="button"
                      className="scc-fact scc-fact--button"
                      title="Open manager's page"
                      onClick={() =>
                        navigate(
                          `/bdr-office/clients/view-client/${clientObj.manager._id}`,
                        )
                      }
                    >
                      <span className="scc-fact-icon">
                        <PeopleIcon />
                      </span>
                      <span className="scc-fact-body">
                        <span className="bdr-field-label">MANAGED BY</span>
                        <span className="scc-fact-value">
                          {clientObj?.manager?.firstName}{" "}
                          {clientObj?.manager?.lastName}
                        </span>
                      </span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="scc-fact scc-fact--button"
                      title="Link this client to a manager"
                      onClick={() => setManagerModalOpen(true)}
                    >
                      <span className="scc-fact-icon">
                        <PeopleIcon />
                      </span>
                      <span className="scc-fact-body">
                        <span className="bdr-field-label">MANAGED BY</span>
                        <span className="scc-fact-value scc-fact-value--empty">
                          Add manager
                        </span>
                      </span>
                    </button>
                  )}
                </div>

                {/* NO Delete here. Deleting a client is not a surface-level
                    action — it sat alone at the rail's bottom costing a row of
                    a column that has to fit without scrolling, for something
                    nobody should reach for by accident. It MOVED to the
                    edit/create-client modal (2026-10-02), as promised when the
                    Edit branch still carried it. */}

                {/*
                 * "Add manager". `size="sm"` (420px) rather than the 640px
                 * default — the body is one search field, and the slot inside
                 * needs 320px, which clears 420 minus padding with room to
                 * spare. ManagerLinkContainer is rendered as-is rather than
                 * copied: it owns the link mutation, the undo snack and the
                 * `forManagerOfClientId` search filter, and a second copy of
                 * that flow is exactly what this pass keeps removing.
                 *
                 * `panelClassName` carries the reserved height the results list
                 * needs — see `.scc-manager-modal`. The list renders IN PLACE
                 * (MUI Popper, `disablePortal`), so the only thing that keeps
                 * it out of `.bdr-modal-body`'s clip is the body being tall
                 * enough to hold it. Portalling it instead looks like the
                 * obvious fix and is not: its CSS is descendant-scoped.
                 */}
                {managerModalOpen && (
                  <BdrModal
                    open
                    onClose={() => setManagerModalOpen(false)}
                    title="Add manager"
                    variant="panel"
                    size="sm"
                    panelClassName="scc-manager-modal"
                  >
                    <ManagerLinkContainer
                      clientObj={clientObj}
                      onMutated={fetchClientInfo}
                      hostedInModal
                      onLinked={() => setManagerModalOpen(false)}
                    />
                  </BdrModal>
                )}
              </aside>

              <main className="scc-main">
                <ClientConsoleSummary summary={summary} />
                <ClientConsoleTiles
                  tiles={consoleTiles}
                  activeKey={activeSection}
                  onSelect={selectSection}
                />
                <ClientConsoleVS
                  activeKey={activeSection}
                  order={SECTION_ORDER}
                  renderPanel={renderSectionPanel}
                />
              </main>
            </div>
        )}
    </div>
  );
};

export default SingleClient;
